import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useState, useCallback, useEffect } from 'react';
import {
  Loader2,
  User,
  Mail,
  Calendar,
  Shield,
  Smartphone,
  ShieldCheck,
  Copy,
  Check,
  Bell,
  MailIcon,
  Megaphone,
  Monitor,
  Tablet,
  Key,
  LogOut,
  Globe,
  Clock,
  TriangleAlert,
  History,
} from 'lucide-react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import * as _UAParser from 'ua-parser-js';
// CJS/ESM interop: ua-parser-js can be called as function returning IResult
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseUA = ((_UAParser as any).default ?? _UAParser) as (ua: string) => {
  browser: { name?: string; major?: string; version?: string };
  os: { name?: string; version?: string };
  device: { type?: string };
};
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/auth/password-input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { OtpInput } from '@/components/ui/otp-input';
import {
  useUserProfile,
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '@/hooks/api/use-onboarding';
import { usePasswordStrength } from '@/hooks/use-password-strength';
import { authClient, useSession } from '@/lib/auth-client';
import { api } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { userKeys } from '@/lib/query-keys';

export const Route = createFileRoute('/_authenticated/profile')({
  component: ProfilePage,
});

type Tab = 'account' | 'security' | 'notifications';

function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { data: profile, isPending } = useUserProfile();
  const [activeTab, setActiveTab] = useState<Tab>('account');

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const dateLocale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
  const createdDate = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString(dateLocale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  const initials = profile?.name
    ? profile.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  const tabs: { key: Tab; labelKey: string; icon: typeof User }[] = [
    { key: 'account', labelKey: 'profile.tabAccount', icon: User },
    { key: 'security', labelKey: 'profile.tabSecurity', icon: Shield },
    { key: 'notifications', labelKey: 'profile.tabNotifications', icon: Bell },
  ];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">{t('profile.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('profile.subtitle')}</p>
      </div>

      {/* Profile hero card */}
      <Card>
        <CardContent className="flex items-center gap-5 py-6">
          <div className="flex size-16 shrink-0 items-center justify-center rounded-full border-2 border-border bg-muted text-xl font-semibold">
            {initials}
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">{profile?.name || '—'}</h2>
            <p className="text-sm text-muted-foreground">{profile?.email}</p>
            <div className="mt-2 flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-600 dark:text-green-400">
                <span className="size-1.5 rounded-full bg-green-500" />
                {t('profile.accountActive')}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex rounded-lg border border-border">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'flex flex-1 cursor-pointer items-center justify-center gap-2 py-3 text-sm font-medium transition-colors',
                activeTab === tab.key
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                tab.key === 'account' && 'rounded-l-lg',
                tab.key === 'notifications' && 'rounded-r-lg',
              )}
            >
              <Icon className="size-4" />
              {t(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'account' && (
        <Card>
          <CardContent className="py-5">
            <div className="mb-1">
              <h3 className="text-base font-semibold">{t('profile.accountInfo')}</h3>
              <p className="text-sm text-muted-foreground">{t('profile.accountInfoDesc')}</p>
            </div>

            <div className="mt-5 divide-y divide-border">
              <ProfileField icon={User} label={t('profile.name')} value={profile?.name || '—'} />
              <ProfileField icon={Mail} label={t('profile.email')} value={profile?.email || '—'} />
              <ProfileField icon={Calendar} label={t('profile.memberSince')} value={createdDate} />
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'security' && (
        <div className="space-y-6">
          <ChangePasswordSection />
          <AdditionalSecuritySection />
        </div>
      )}

      {activeTab === 'notifications' && <NotificationsSection />}
    </div>
  );
}

function ProfileField({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof User;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-4 py-4">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
        <Icon className="size-4 text-muted-foreground" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}

function NotificationsSection() {
  const { t } = useTranslation();
  const { data: prefs, isPending } = useNotificationPreferences();
  const { mutate: updatePrefs } = useUpdateNotificationPreferences();

  function handleToggle(key: 'emailNotifications' | 'productUpdates', checked: boolean) {
    updatePrefs({ [key]: checked });
  }

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="py-5">
        <div className="mb-1">
          <h3 className="text-base font-semibold">{t('profile.notifications.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('profile.notifications.description')}</p>
        </div>

        <div className="mt-5 divide-y divide-border">
          <div className="flex items-center gap-4 py-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <MailIcon className="size-4 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{t('profile.notifications.emailNotifications')}</p>
              <p className="text-xs text-muted-foreground">
                {t('profile.notifications.emailNotificationsDesc')}
              </p>
            </div>
            <Switch
              checked={prefs?.emailNotifications ?? true}
              onCheckedChange={(checked) => handleToggle('emailNotifications', checked)}
            />
          </div>

          <div className="flex items-center gap-4 py-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <Megaphone className="size-4 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{t('profile.notifications.productUpdates')}</p>
              <p className="text-xs text-muted-foreground">
                {t('profile.notifications.productUpdatesDesc')}
              </p>
            </div>
            <Switch
              checked={prefs?.productUpdates ?? true}
              onCheckedChange={(checked) => handleToggle('productUpdates', checked)}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChangePasswordSection() {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const strengthScore = usePasswordStrength(newPassword);

  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    strengthScore >= 2 &&
    newPassword === confirmPassword;

  const passwordMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      const { error } = await authClient.changePassword({
        newPassword,
        currentPassword,
        revokeOtherSessions: false,
      });

      if (error) {
        if (error.code === 'INVALID_PASSWORD') {
          toast.error(t('profile.wrongCurrentPassword'));
        } else {
          toast.error(error.message || t('profile.passwordChangeError'));
        }
        return;
      }

      toast.success(t('profile.passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      toast.error(t('profile.passwordChangeError'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Card>
      <CardContent className="py-5">
        <div className="mb-1">
          <h3 className="text-base font-semibold">{t('profile.changePassword')}</h3>
          <p className="text-sm text-muted-foreground">{t('profile.changePasswordDesc')}</p>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-3">
          <div className="space-y-1.5">
            <label htmlFor="currentPassword" className="text-sm font-medium">
              {t('profile.currentPassword')}
            </label>
            <PasswordInput
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder={t('profile.currentPasswordPlaceholder')}
              autoComplete="current-password"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="newPassword" className="text-sm font-medium">
              {t('profile.newPassword')}
            </label>
            <PasswordInput
              id="newPassword"
              showStrength
              strengthScore={strengthScore}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('profile.newPasswordPlaceholder')}
              autoComplete="new-password"
            />
            {newPassword.length > 0 && newPassword.length < 8 && (
              <p className="text-xs text-destructive">{t('profile.passwordMinLength')}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="confirmPassword" className="text-sm font-medium">
              {t('profile.confirmPassword')}
            </label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('profile.confirmPasswordPlaceholder')}
              autoComplete="new-password"
            />
            {passwordMismatch && (
              <p className="text-xs text-destructive">{t('profile.passwordMismatch')}</p>
            )}
          </div>

          <Button type="submit" disabled={!canSubmit || isSubmitting} className="cursor-pointer">
            {isSubmitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('profile.changingPassword')}
              </>
            ) : (
              t('profile.changePasswordButton')
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Additional Security Section — 2FA toggle + setup/disable flow
// ============================================================

type SetupStep = 'password' | 'qr' | 'success';

function AdditionalSecuritySection() {
  const { t } = useTranslation();
  const { data: session, refetch: refetchSession } = useSession();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [showLoginHistoryModal, setShowLoginHistoryModal] = useState(false);

  const is2FAEnabled = session?.user?.twoFactorEnabled ?? false;

  function handleToggle(checked: boolean) {
    if (checked) {
      setShowSetupModal(true);
    } else {
      setShowDisableModal(true);
    }
  }

  return (
    <>
      <Card>
        <CardContent className="py-5">
          <div className="mb-1">
            <h3 className="text-base font-semibold">{t('profile.securitySection')}</h3>
            <p className="text-sm text-muted-foreground">{t('profile.securitySectionDesc')}</p>
          </div>

          <div className="mt-5 divide-y divide-border">
            {/* 2FA row */}
            <div className="flex items-center gap-4 py-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                <Smartphone className="size-4 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{t('profile.twoFactor')}</p>
                <p className="text-xs text-muted-foreground">{t('profile.twoFactorDesc')}</p>
              </div>
              <Switch checked={is2FAEnabled} onCheckedChange={handleToggle} />
            </div>

            {/* Active sessions row */}
            <div className="flex items-center gap-4 py-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                <Key className="size-4 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{t('profile.activeSessions.sectionTitle')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('profile.activeSessions.sectionDesc')}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSessionsModal(true)}
                className="cursor-pointer"
              >
                {t('profile.activeSessions.manage')}
              </Button>
            </div>

            {/* Login history row */}
            <div className="flex items-center gap-4 py-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                <History className="size-4 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{t('profile.loginHistory.sectionTitle')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('profile.loginHistory.sectionDesc')}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLoginHistoryModal(true)}
                className="cursor-pointer"
              >
                {t('profile.loginHistory.view')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Setup2FAModal
        open={showSetupModal}
        onOpenChange={setShowSetupModal}
        onSuccess={() => refetchSession()}
      />
      <Disable2FAModal
        open={showDisableModal}
        onOpenChange={setShowDisableModal}
        onSuccess={() => refetchSession()}
      />
      <ActiveSessionsModal open={showSessionsModal} onOpenChange={setShowSessionsModal} />
      <LoginHistoryModal open={showLoginHistoryModal} onOpenChange={setShowLoginHistoryModal} />
    </>
  );
}

// ============================================================
// Setup 2FA Modal — password → QR code → verify → backup codes
// ============================================================

function Setup2FAModal({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<SetupStep>('password');
  const [password, setPassword] = useState('');
  const [totpURI, setTotpURI] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  function resetState() {
    setStep('password');
    setPassword('');
    setTotpURI('');
    setCode('');
    setBackupCodes([]);
    setError(null);
    setCopied(false);
  }

  function handleClose(value: boolean) {
    if (!value) resetState();
    onOpenChange(value);
  }

  async function handlePasswordSubmit() {
    if (!password || isLoading) return;
    setError(null);
    setIsLoading(true);

    try {
      const result = await authClient.twoFactor.enable({ password });

      if (result.error) {
        setError(t('profile.setup2FA.error'));
        return;
      }

      // Get TOTP URI for QR code
      const uriResult = await authClient.twoFactor.getTotpUri({ password });

      if (uriResult.error || !uriResult.data?.totpURI) {
        setError(t('profile.setup2FA.error'));
        return;
      }

      setTotpURI(uriResult.data.totpURI);
      setStep('qr');
    } catch {
      setError(t('profile.setup2FA.error'));
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerify() {
    if (!code || isLoading) return;
    setError(null);
    setIsLoading(true);

    try {
      const result = await authClient.twoFactor.verifyTotp({ code });

      if (result.error) {
        setError(t('profile.setup2FA.invalidCode'));
        return;
      }

      // Get backup codes — requires password for security
      try {
        const codesResult = await authClient.twoFactor.generateBackupCodes({
          password,
        });
        if (codesResult.data?.backupCodes) {
          setBackupCodes(codesResult.data.backupCodes);
        }
      } catch {
        // Backup codes generation may fail — still show success
      }

      setStep('success');
      onSuccess();
    } catch {
      setError(t('profile.setup2FA.invalidCode'));
    } finally {
      setIsLoading(false);
    }
  }

  const handleCopyBackupCodes = useCallback(() => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [backupCodes]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="size-5 shrink-0 text-primary" />
            {t('profile.setup2FA.title')}
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Password */}
        {step === 'password' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('profile.setup2FA.enterPassword')}</p>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('profile.setup2FA.passwordPlaceholder')}
              autoComplete="current-password"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handlePasswordSubmit();
                }
              }}
              autoFocus
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleClose(false)}
                className="cursor-pointer"
              >
                {t('team.deactivateModal.cancel')}
              </Button>
              <Button
                onClick={handlePasswordSubmit}
                disabled={!password || isLoading}
                className="cursor-pointer"
              >
                {isLoading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  t('profile.setup2FA.continue')
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 2: QR Code + TOTP verification */}
        {step === 'qr' && (
          <div className="space-y-4">
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">1.</span>{' '}
                {t('profile.setup2FA.step1')}
              </p>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">2.</span>{' '}
                {t('profile.setup2FA.step2')}
              </p>
            </div>

            {/* QR Code + manual key */}
            {totpURI && (
              <div className="space-y-3">
                <div className="flex justify-center rounded-lg border bg-white p-4">
                  <QRCode uri={totpURI} />
                </div>
                <ManualKeyDisplay uri={totpURI} />
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">3.</span> {t('profile.setup2FA.step3')}
            </p>

            <div>
              <label className="mb-4 block text-sm font-medium">
                {t('profile.setup2FA.verifyCode')}
              </label>
              <OtpInput value={code} onChange={setCode} />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setStep('password');
                  setCode('');
                  setError(null);
                }}
                className="cursor-pointer"
              >
                {t('team.reassignment.back')}
              </Button>
              <Button
                onClick={handleVerify}
                disabled={code.length < 6 || isLoading}
                className="cursor-pointer"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    {t('profile.setup2FA.verifying')}
                  </>
                ) : (
                  t('profile.setup2FA.verify')
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 3: Success + Backup Codes */}
        {step === 'success' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-lg border border-green-500/50 bg-green-50 p-3 dark:bg-green-950/20">
              <ShieldCheck className="size-5 shrink-0 text-green-600 dark:text-green-400" />
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  {t('profile.setup2FA.successTitle')}
                </p>
                <p className="text-xs text-green-700 dark:text-green-300">
                  {t('profile.setup2FA.successDesc')}
                </p>
              </div>
            </div>

            {backupCodes.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{t('profile.setup2FA.backupCodes')}</p>
                  <button
                    type="button"
                    onClick={handleCopyBackupCodes}
                    className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {copied ? (
                      <>
                        <Check className="size-3" />
                        {t('profile.setup2FA.copied')}
                      </>
                    ) : (
                      <>
                        <Copy className="size-3" />
                        {t('profile.setup2FA.copyAll')}
                      </>
                    )}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1.5 rounded-lg border bg-muted/30 p-3">
                  {backupCodes.map((bc, i) => (
                    <code key={i} className="text-center text-sm font-mono">
                      {bc}
                    </code>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('profile.setup2FA.backupCodesWarning')}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button onClick={() => handleClose(false)} className="cursor-pointer">
                {t('profile.setup2FA.done')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Disable 2FA Modal — confirm with password
// ============================================================

function Disable2FAModal({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleClose(value: boolean) {
    if (!value) {
      setPassword('');
      setError(null);
    }
    onOpenChange(value);
  }

  async function handleConfirm() {
    if (!password || isLoading) return;
    setError(null);
    setIsLoading(true);

    try {
      const result = await authClient.twoFactor.disable({ password });

      if (result.error) {
        if (result.error.code === 'INVALID_PASSWORD') {
          setError(t('profile.disable2FAModal.wrongPassword'));
        } else {
          setError(t('profile.disable2FAModal.error'));
        }
        return;
      }

      toast.success(t('profile.disable2FAModal.success'));
      onSuccess();
      handleClose(false);
    } catch {
      setError(t('profile.disable2FAModal.error'));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('profile.disable2FAModal.title')}</DialogTitle>
          <DialogDescription>{t('profile.disable2FAModal.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="disable-2fa-password" className="text-sm font-medium">
              {t('profile.disable2FAModal.password')}
            </label>
            <PasswordInput
              id="disable-2fa-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('profile.disable2FAModal.passwordPlaceholder')}
              autoComplete="current-password"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleConfirm();
                }
              }}
              autoFocus
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} className="cursor-pointer">
            {t('team.deactivateModal.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!password || isLoading}
            className="cursor-pointer"
          >
            {isLoading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('profile.disable2FAModal.confirming')}
              </>
            ) : (
              t('profile.disable2FAModal.confirm')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// QR Code component — renders TOTP URI as QR code locally
// ============================================================

function QRCode({ uri }: { uri: string }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    import('qrcode').then((QRCodeLib) => {
      QRCodeLib.toDataURL(uri, { width: 200, margin: 1 }).then(setDataUrl);
    });
  }, [uri]);

  if (!dataUrl) {
    return (
      <div className="flex size-[200px] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <img src={dataUrl} alt="QR Code" width={200} height={200} className="rounded" />;
}

// ============================================================
// Manual key display — extract secret from TOTP URI for manual entry
// ============================================================

function ManualKeyDisplay({ uri }: { uri: string }) {
  const { t } = useTranslation();
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const secret = (() => {
    try {
      const url = new URL(uri);
      return url.searchParams.get('secret') ?? '';
    } catch {
      return '';
    }
  })();

  if (!secret) return null;

  // Format secret in groups of 4 for readability
  const formatted = secret.replace(/(.{4})/g, '$1 ').trim();

  function handleCopy() {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setShowKey((v) => !v)}
        className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {showKey ? t('profile.setup2FA.hideKey') : t('profile.setup2FA.showKey')}
      </button>
      {showKey && (
        <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
          <code className="flex-1 text-xs font-mono tracking-wider break-all">{formatted}</code>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 cursor-pointer text-muted-foreground hover:text-foreground transition-colors"
          >
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Active Sessions Modal — list, revoke individual, revoke all
// ============================================================

function parseUserAgent(ua: string) {
  const result = parseUA(ua);

  return {
    browser: result.browser.name
      ? `${result.browser.name}${result.browser.major ? ` ${result.browser.major}` : ''}`
      : null,
    os: result.os.name
      ? `${result.os.name}${result.os.version ? ` ${result.os.version}` : ''}`
      : null,
    deviceType: (result.device.type as 'mobile' | 'tablet' | undefined) ?? 'desktop',
  };
}

function getDeviceIcon(deviceType: string) {
  switch (deviceType) {
    case 'mobile':
      return Smartphone;
    case 'tablet':
      return Tablet;
    default:
      return Monitor;
  }
}

function formatRelativeTime(date: Date, locale: string): string {
  const now = Date.now();
  const diff = now - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return locale === 'pl' ? 'Aktywna teraz' : 'Active now';

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (minutes < 60) return rtf.format(-minutes, 'minute');
  if (hours < 24) return rtf.format(-hours, 'hour');
  if (days < 30) return rtf.format(-days, 'day');

  return date.toLocaleDateString(locale === 'pl' ? 'pl-PL' : 'en-US', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

interface SessionItem {
  id: string;
  token: string;
  userAgent?: string | null;
  ipAddress?: string | null;
  updatedAt: Date;
  createdAt: Date;
}

function ActiveSessionsModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const {
    data: sessions,
    isLoading,
    error,
  } = useQuery({
    queryKey: userKeys.sessions(),
    queryFn: async () => {
      const result = await authClient.listSessions();
      if (result.error) throw new Error(result.error.message);
      return (result.data ?? []) as SessionItem[];
    },
    enabled: open,
    staleTime: 30_000,
  });

  const currentSessionToken = session?.session?.token;

  // Deduplicate: keep only the most recent session per user-agent.
  // Multiple logins from the same browser create separate rows — users
  // expect to see one entry per device, like Google/GitHub.
  const deduplicatedSessions = (() => {
    const all = [...(sessions ?? [])].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
    const seen = new Map<string, SessionItem>();
    for (const s of all) {
      const key = s.userAgent ?? s.id; // fallback to id if no UA
      // Always keep the current session, even if another session shares the same UA
      if (s.token === currentSessionToken) {
        seen.set(`__current__`, s);
      } else if (!seen.has(key)) {
        seen.set(key, s);
      }
    }
    return [...seen.values()];
  })();

  // Sort: current session first, then by updatedAt descending
  const sortedSessions = deduplicatedSessions.sort((a, b) => {
    if (a.token === currentSessionToken) return -1;
    if (b.token === currentSessionToken) return 1;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const otherSessions = sortedSessions.filter((s) => s.token !== currentSessionToken);

  async function handleRevoke(sessionToken: string, sessionId: string) {
    setRevokingId(sessionId);
    try {
      const result = await authClient.revokeSession({ token: sessionToken });
      if (result.error) {
        toast.error(t('profile.activeSessions.revokeError'));
        return;
      }
      toast.success(t('profile.activeSessions.revokeSuccess'));
      queryClient.invalidateQueries({ queryKey: userKeys.sessions() });
    } catch {
      toast.error(t('profile.activeSessions.revokeError'));
    } finally {
      setRevokingId(null);
    }
  }

  async function handleRevokeAll() {
    setRevokingAll(true);
    try {
      const result = await authClient.revokeOtherSessions();
      if (result.error) {
        toast.error(t('profile.activeSessions.revokeAllError'));
        return;
      }
      toast.success(t('profile.activeSessions.revokeAllSuccess'));
      queryClient.invalidateQueries({ queryKey: userKeys.sessions() });
    } catch {
      toast.error(t('profile.activeSessions.revokeAllError'));
    } finally {
      setRevokingAll(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Monitor className="size-5 shrink-0" />
            {t('profile.activeSessions.title')}
          </DialogTitle>
          <DialogDescription>{t('profile.activeSessions.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive text-center py-4">
              {t('profile.activeSessions.loadError')}
            </p>
          )}

          {!isLoading &&
            !error &&
            sortedSessions.map((s) => {
              const isCurrent = s.token === currentSessionToken;
              const ua = parseUserAgent(s.userAgent ?? '');
              const DeviceIcon = getDeviceIcon(ua.deviceType);
              const browserLabel = ua.browser ?? t('profile.activeSessions.unknownBrowser');
              const osLabel = ua.os ?? t('profile.activeSessions.unknownOS');
              const lastActive = formatRelativeTime(new Date(s.updatedAt), i18n.language);

              return (
                <div
                  key={s.id}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-3',
                    isCurrent && 'border-green-500/50 bg-green-500/5',
                  )}
                >
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                    <DeviceIcon className="size-4 text-muted-foreground" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">
                        {browserLabel} na {osLabel}
                      </p>
                      {isCurrent && (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-600 dark:text-green-400">
                          <span className="size-1.5 rounded-full bg-green-500" />
                          {t('profile.activeSessions.currentSession')}
                        </span>
                      )}
                    </div>

                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      {s.ipAddress && (
                        <span className="inline-flex items-center gap-1">
                          <Globe className="size-3" />
                          {s.ipAddress}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1">
                        <Clock className="size-3" />
                        {lastActive}
                      </span>
                    </div>
                  </div>

                  {!isCurrent && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevoke(s.token, s.id)}
                      disabled={revokingId === s.id}
                      className="shrink-0 cursor-pointer text-muted-foreground hover:text-destructive"
                    >
                      {revokingId === s.id ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <>
                          <LogOut className="size-4" />
                          {t('profile.activeSessions.revoke')}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
        </div>

        {/* Revoke all others */}
        {otherSessions.length > 0 && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-3">
            <div className="flex items-start gap-2">
              <TriangleAlert className="size-4 shrink-0 text-destructive mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-medium">{t('profile.activeSessions.revokeAllOthers')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('profile.activeSessions.revokeAllOthersDesc')}
                </p>
              </div>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleRevokeAll}
              disabled={revokingAll}
              className="w-full cursor-pointer"
            >
              {revokingAll ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                t('profile.activeSessions.revokeAllOthersConfirm')
              )}
            </Button>
          </div>
        )}

        {!isLoading && !error && otherSessions.length === 0 && sessions && sessions.length > 0 && (
          <p className="text-sm text-muted-foreground text-center py-2">
            {t('profile.activeSessions.noOtherSessions')}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Login History Modal — chronological list of past sign-ins
// ============================================================

interface LoginHistoryItem {
  id: string;
  ipAddress: string | null;
  userAgent: string | null;
  browser: string | null;
  os: string | null;
  deviceType: string;
  createdAt: string;
}

interface LoginHistoryResponse {
  items: LoginHistoryItem[];
  total: number;
}

function LoginHistoryModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: userKeys.loginHistory(page),
    queryFn: () =>
      api.get<{ data: LoginHistoryResponse }>(`/users/me/login-history?page=${page}&perPage=20`),
    select: (res) => res.data,
    enabled: open,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  // Group items by date
  const groups = (() => {
    const map = new Map<string, LoginHistoryItem[]>();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86_400_000);

    for (const item of items) {
      const date = new Date(item.createdAt);
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      let label: string;
      if (dateOnly.getTime() === today.getTime()) {
        label = i18n.language === 'pl' ? 'Dzisiaj' : 'Today';
      } else if (dateOnly.getTime() === yesterday.getTime()) {
        label = i18n.language === 'pl' ? 'Wczoraj' : 'Yesterday';
      } else {
        label = dateOnly.toLocaleDateString(i18n.language === 'pl' ? 'pl-PL' : 'en-US', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      }

      const existing = map.get(label);
      if (existing) {
        existing.push(item);
      } else {
        map.set(label, [item]);
      }
    }

    return Array.from(map.entries()).map(([label, entries]) => ({ label, entries }));
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="size-5 shrink-0" />
            {t('profile.loginHistory.title')}
          </DialogTitle>
          <DialogDescription>{t('profile.loginHistory.description')}</DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            'max-h-[400px] overflow-y-auto transition-opacity',
            isFetching && !isLoading && 'opacity-50',
          )}
        >
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive text-center py-4">
              {t('profile.loginHistory.loadError')}
            </p>
          )}

          {!isLoading && !error && items.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              {t('profile.loginHistory.empty')}
            </p>
          )}

          {!isLoading &&
            !error &&
            groups.map((group) => (
              <div key={group.label}>
                <div className="sticky top-0 z-10 bg-background px-1 py-1.5">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
                    {group.label}
                  </span>
                </div>
                <div className="space-y-2 mb-3">
                  {group.entries.map((entry) => {
                    const DeviceIcon = getDeviceIcon(entry.deviceType);
                    const browserLabel =
                      entry.browser ?? t('profile.activeSessions.unknownBrowser');
                    const osLabel = entry.os ?? t('profile.activeSessions.unknownOS');
                    const time = new Date(entry.createdAt).toLocaleTimeString(
                      i18n.language === 'pl' ? 'pl-PL' : 'en-US',
                      { hour: '2-digit', minute: '2-digit' },
                    );

                    return (
                      <div key={entry.id} className="flex items-start gap-3 rounded-lg border p-3">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                          <DeviceIcon className="size-4 text-muted-foreground" />
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">
                            {browserLabel} {t('profile.loginHistory.on')} {osLabel}
                          </p>

                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                            {entry.ipAddress && (
                              <span className="inline-flex items-center gap-1">
                                <Globe className="size-3" />
                                {entry.ipAddress}
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1">
                              <Clock className="size-3" />
                              {time}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-2 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isLoading}
              className="cursor-pointer"
            >
              {t('profile.loginHistory.prev')}
            </Button>
            <span className="text-xs text-muted-foreground px-2">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || isLoading}
              className="cursor-pointer"
            >
              {t('profile.loginHistory.next')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
